<!-- Main Loading Overlay -->
<app-overlay-loader *ngIf="loading"></app-overlay-loader>

<!-- Header Section -->
<div class="practice-cases-header">
  <h2>Practice Cases Management</h2>
  <p>Manage practice cases and diagnosis categories for the system</p>
</div>

<!-- Action Buttons -->
<div class="action-buttons">
  <button class="btn btn-primary" (click)="openCaseModal('add')">
    <i class="fas fa-plus"></i> Add Practice Case
  </button>
  <button class="btn btn-secondary" (click)="openDiagnosisModal('add')">
    <i class="fas fa-plus"></i> Add Diagnosis Category
  </button>
</div>

<!-- Diagnosis Categories Section -->
<div class="section-container">
  <div class="section-header">
    <h3>Diagnosis Categories</h3>
    <span class="count-badge">{{ diagnosisCategories.length }} categories</span>
  </div>
  
  <div class="table-container">
    <table class="data-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Diagnosis Title</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let diagnosis of diagnosisCategories; let i = index" class="table-row">
          <td>{{ i + 1 }}</td>
          <td>{{ diagnosis.diagnosis_title }}</td>
          <td class="actions-cell">
            <button class="btn-icon edit" (click)="openDiagnosisModal('edit', diagnosis)" title="Edit">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn-icon delete" (click)="deleteDiagnosisCategory(diagnosis)" title="Delete">
              <i class="fas fa-trash"></i>
            </button>
          </td>
        </tr>
        <tr *ngIf="diagnosisCategories.length === 0" class="no-data-row">
          <td colspan="3">No diagnosis categories found</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Practice Cases Section -->
<div class="section-container">
  <div class="section-header">
    <h3>Practice Cases</h3>
    <span class="count-badge">{{ practiceCases.length }} cases</span>
  </div>
  
  <div class="table-container">
    <table class="data-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Case Title</th>
          <th>Diagnosis</th>
          <th>Content</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let practiceCase of practiceCases; let i = index" class="table-row">
          <td>{{ i + 1 }}</td>
          <td>{{ practiceCase.case_title }}</td>
          <td>
            <span class="diagnosis-badge">{{ practiceCase.diagnosis_title }}</span>
          </td>
          <td class="content-cell">
            <button class="btn-view-content" (click)="viewCaseContent(practiceCase)" title="View Content">
              <i class="fas fa-eye"></i> View Content
            </button>
          </td>
          <td class="actions-cell">
            <button class="btn-icon edit" (click)="openCaseModal('edit', practiceCase)" title="Edit">
              <i class="fas fa-edit"></i>
            </button>
            <button class="btn-icon delete" (click)="deletePracticeCase(practiceCase)" title="Delete">
              <i class="fas fa-trash"></i>
            </button>
          </td>
        </tr>
        <tr *ngIf="practiceCases.length === 0" class="no-data-row">
          <td colspan="5">No practice cases found</td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- Practice Case Modal -->
<app-overlay-loader *ngIf="modalLoader"></app-overlay-loader>
<p-dialog 
  [(visible)]="showCaseModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  styleClass="custom-dialog"
  [style]="{width: '800px'}"
  (onHide)="closeCaseModal()">
  
  <ng-template pTemplate="header">
    <h4>{{ modalType === 'add' ? 'Add' : 'Edit' }} Practice Case</h4>
  </ng-template>
  
  <div class="modal-content">
    
    
    <form #caseFormRef="ngForm">
      <div class="form-group">
        <label for="caseTitle">Case Title *</label>
        <input 
          type="text" 
          id="caseTitle"
          class="form-control"
          [(ngModel)]="caseForm.case_title"
          name="caseTitle"
          required
          placeholder="Enter case title">
      </div>
      
      <div class="form-group">
        <label for="diagnosisSelect">Diagnosis Category *</label>
        <select 
          id="diagnosisSelect"
          class="form-control"
          [(ngModel)]="caseForm.diagnosis_id"
          name="diagnosisSelect"
          required>
          <option value="">Select diagnosis category</option>
          <option *ngFor="let diagnosis of diagnosisCategories" [value]="diagnosis.diagnosis_id">
            {{ diagnosis.diagnosis_title }}
          </option>
        </select>
      </div>
      
      <div class="form-group">
        <label for="caseContent">Case Content *</label>
        <div class="editor-help-text">
          <small class="text-muted">
            <i class="fas fa-info-circle"></i>
            You can add images, videos, tables, and rich formatting to your case content. 
            Use the image upload button in the toolbar to add media files.
          </small>
        </div>
        <div class="editor-container" id="ckEditorContainer">
          <ckeditor id="ckEditorNotebook"></ckeditor>
        </div>
      </div>
    </form>
  </div>
  
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeCaseModal()">Cancel</button>
      <button class="btn btn-primary" (click)="savePracticeCase()" [disabled]="modalLoader">
        {{ modalType === 'add' ? 'Add' : 'Update' }} Case
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- Diagnosis Category Modal -->
<p-dialog 
  [(visible)]="showDiagnosisModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  styleClass="custom-dialog"
  [style]="{width: '400px'}"
  (onHide)="closeDiagnosisModal()">
  
  <ng-template pTemplate="header">
    <h4>{{ modalType === 'add' ? 'Add' : 'Edit' }} Diagnosis Category</h4>
  </ng-template>
  
  <div class="modal-content">
    <app-overlay-loader *ngIf="modalLoader"></app-overlay-loader>
    
    <form #diagnosisFormRef="ngForm">
      <div class="form-group">
        <label for="diagnosisTitle">Diagnosis Title *</label>
        <input 
          type="text" 
          id="diagnosisTitle"
          class="form-control"
          [(ngModel)]="diagnosisForm.diagnosis_title"
          name="diagnosisTitle"
          required
          placeholder="Enter diagnosis title">
      </div>
    </form>
  </div>
  
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeDiagnosisModal()">Cancel</button>
      <button class="btn btn-primary" (click)="saveDiagnosisCategory()" [disabled]="modalLoader">
        {{ modalType === 'add' ? 'Add' : 'Update' }} Category
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- Delete Confirmation Modal -->
<p-dialog 
  [(visible)]="showDeleteConfirmModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  styleClass="custom-dialog delete-dialog"
  [style]="{width: '400px'}"
  (onHide)="closeDeleteModal()">
  
  <ng-template pTemplate="header">
    <h4>Confirm Delete</h4>
  </ng-template>
  
  <div class="modal-content">
    <app-overlay-loader *ngIf="modalLoader"></app-overlay-loader>
    
    <div class="delete-confirmation">
      <p>Are you sure you want to delete this {{ deleteType === 'case' ? 'practice case' : 'diagnosis category' }}?</p>
      <p class="item-name">{{ itemToDelete?.case_title || itemToDelete?.diagnosis_title }}</p>
      <p>This action cannot be undone.</p>
    </div>
  </div>
  
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeDeleteModal()">Cancel</button>
      <button class="btn btn-primary" (click)="confirmDelete()" [disabled]="modalLoader">
        Delete
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- View Content Modal -->
<p-dialog 
  [(visible)]="showViewContentModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  styleClass="custom-dialog view-content-dialog"
  [style]="{width: '900px', maxHeight: '80vh'}"
  (onHide)="closeViewContentModal()">
  
  <ng-template pTemplate="header">
    <h4>{{ selectedCase?.case_title }}</h4>
  </ng-template>
  
  <div class="view-content-modal">
    <div class="case-info">
      <div class="info-item">
        <strong>Case #:</strong> {{ selectedCaseIndex }}
      </div>
      <div class="info-item">
        <strong>Diagnosis:</strong> 
        <span class="diagnosis-badge">{{ selectedCase?.diagnosis_title }}</span>
      </div>
    </div>
    
    <div class="content-divider"></div>
    
    <div class="case-content" [innerHTML]="selectedCase?.case_content"></div>
  </div>
  
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="btn btn-secondary" (click)="closeViewContentModal()">Close</button>
      <button class="btn btn-primary" (click)="openCaseModal('edit', selectedCase)">
        <i class="fas fa-edit"></i> Edit Case
      </button>
    </div>
  </ng-template>
</p-dialog>
